import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const STC = { Active: "bg-emerald-100 text-emerald-700", Indisponible: "bg-red-100 text-red-700", Maintenance: "bg-amber-100 text-amber-700", "Archivée": "bg-slate-100 text-slate-500" };

export default function Rooms() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canWrite = hasRole("super_admin", "direction", "administration", "coordinateur");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { room_code: "", room_name: "", capacity: 20, location: "", status: "Active" };
  const [form, setForm] = useState(empty);
  const load = () => api.get("/school/rooms").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try { await api.post("/school/rooms", { ...form, capacity: Number(form.capacity) }); toast.success(t("save")); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="school-rooms">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-slate-900">{t("rooms")}</h1>
        {canWrite && <Button data-testid="room-add-btn" onClick={() => { setForm(empty); setOpen(true); }} className="rounded-full gap-2"><Plus className="h-4 w-4" />{t("add")}</Button>}
      </div>
      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
            <TableHead>{t("roomCode")}</TableHead><TableHead>{t("roomName")}</TableHead><TableHead>{t("capacity")}</TableHead><TableHead>{t("location")}</TableHead><TableHead>{t("status")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid="room-row">
                <TableCell className="font-medium text-slate-800">{r.room_code}</TableCell>
                <TableCell className="text-slate-700">{r.room_name}</TableCell>
                <TableCell className="tabular-nums">{r.capacity}</TableCell>
                <TableCell className="text-slate-500">{r.location || "—"}</TableCell>
                <TableCell><Badge className={`${STC[r.status] || "bg-slate-100"} border-0`}>{r.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{t("add")} — {t("rooms")}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <Input data-testid="room-code" placeholder={t("roomCode")} value={form.room_code} onChange={(e) => setForm({ ...form, room_code: e.target.value })} />
            <Input data-testid="room-name" placeholder={t("roomName")} value={form.room_name} onChange={(e) => setForm({ ...form, room_name: e.target.value })} />
            <Input data-testid="room-capacity" type="number" placeholder={t("capacity")} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
            <Input data-testid="room-location" placeholder={t("location")} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="room-status"><SelectValue /></SelectTrigger>
              <SelectContent>{["Active", "Indisponible", "Maintenance", "Archivée"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button><Button data-testid="room-save-btn" onClick={submit}>{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
